'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApplicationDiscoveryStatus,
  ApplicationDiscoveryStatusChangedSsePayload,
} from '@terra/shared-contracts-applications';
import type {
  SseEventDef,
  SseSubscribe,
  SseSubscribeReconnect,
} from '@terra/shared-sse-contracts';

const DISCOVERY_STATUS_QUERY_KEY = 'discovery-status';

type UseDiscoveryStatusOptions = {
  applicationId: string | undefined;
  subscribe: SseSubscribe;
  subscribeReconnect: SseSubscribeReconnect;
  fetchStatus: (applicationId: string) => Promise<ApplicationDiscoveryStatus>;
  invalidateAssets: () => void;
  extraDiscoveringSignal?: boolean;
  enabled?: boolean;
  sseEventDef: SseEventDef<ApplicationDiscoveryStatusChangedSsePayload>;
};

type UseDiscoveryStatusResult = {
  status: ApplicationDiscoveryStatus;
  isDiscoveryActive: boolean;
  isProcessingActive: boolean;
  isDeduplicatingActive: boolean;
};

export function useDiscoveryStatus({
  applicationId,
  subscribe,
  subscribeReconnect,
  fetchStatus,
  invalidateAssets,
  extraDiscoveringSignal,
  enabled,
  sseEventDef,
}: UseDiscoveryStatusOptions): UseDiscoveryStatusResult {
  const queryClient = useQueryClient();
  const prevStatusRef = useRef<ApplicationDiscoveryStatus | null>(null);

  const queryKey = useMemo(
    () => [DISCOVERY_STATUS_QUERY_KEY, applicationId] as const,
    [applicationId],
  );

  const { data } = useQuery({
    queryKey,
    queryFn: () => {
      if (!applicationId) {
        throw new Error('applicationId is required to fetch discovery status');
      }

      return fetchStatus(applicationId);
    },
    enabled: !!applicationId && enabled !== false,
    refetchInterval: (query) => {
      const status = query.state.data;

      if (
        status === 'queued' ||
        status === 'processing' ||
        status === 'deduplicating'
      ) {
        return 15_000;
      }

      return false;
    },
  });

  const status: ApplicationDiscoveryStatus = data ?? 'idle';

  useEffect(() => {
    if (!applicationId || enabled === false) {
      return;
    }

    const unsubscribe = subscribe(
      sseEventDef,
      (event) => {
        queryClient.setQueryData(queryKey, event.status);
      },
      { filter: (event) => event.applicationId === applicationId },
    );

    return unsubscribe;
  }, [applicationId, enabled, subscribe, sseEventDef, queryClient, queryKey]);

  useEffect(() => {
    if (!applicationId || enabled === false) {
      return;
    }

    return subscribeReconnect(() => {
      void queryClient.invalidateQueries({ queryKey });
    });
  }, [applicationId, enabled, subscribeReconnect, queryClient, queryKey]);

  useEffect(() => {
    prevStatusRef.current = null;
  }, [applicationId]);

  useEffect(() => {
    const prev = prevStatusRef.current;

    prevStatusRef.current = status;

    if (prev === null) {
      return;
    }

    const wasActive =
      prev === 'processing' ||
      prev === 'queued' ||
      prev === 'discovering' ||
      prev === 'deduplicating';
    const isNowIdle = status === 'idle';

    if (wasActive && isNowIdle) {
      invalidateAssets();
    }
  }, [status, invalidateAssets]);

  const isProcessingActive = status === 'processing' || status === 'queued';
  const isDeduplicatingActive = status === 'deduplicating';
  const isDiscoveryActive =
    status === 'discovering' || (extraDiscoveringSignal ?? false);

  return {
    status,
    isDiscoveryActive,
    isProcessingActive,
    isDeduplicatingActive,
  };
}
